import React, { useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  createStyles,
  Divider,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import PaperColumn from '../components/PaperColumn';
import ApiContext from '../contexts/api';
import { BillingApi } from '../clients/BillingApi';
import { CardPaymentsApi } from '../clients/CardPaymentsApi';
import { UriParams } from '../models/UriParams';
import { PageError } from '../extensions/PageError';
import { useMount } from '../hooks/useMount';
import LoadingSpinner from '../components/LoadingSpinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { useConfiguration } from '../contexts/configuration';
import { CheckoutCommand } from '../models/CheckoutCommand';
import { InvoiceDTO, InvoiceType } from '../models/InvoiceDTO';
import { InvoiceHeader } from '../components/InvoiceHeader';
import { UserInfo } from '../clients/OAuthApiClient';
import { getAppInsights } from '../TelemetryService';
import InvoiceContent from '../components/InvoiceContent';
import GuestInvoiceContent from '../components/GuestInvoiceContent';
import { withTransaction } from '@elastic/apm-rum-react';
import { useAuthentication } from '@zen/identity-lib';
import { useInvoice } from '../contexts/InvoiceContext';
import { Alert } from '@material-ui/lab';
import AmountOverCardLimitWarning from '../components/AmountOverCardLimitWarning';

import { PaymentMessages } from '../models/PaymentMessages';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    divider: {
      margin: theme.spacing(2, 0),
    },
    header: {
      textAlign: 'center',
      marginTop: theme.spacing(2),
    },
    alert: {
      justifyContent: 'center',
      marginTop: theme.spacing(2),
    },
    button: {
      color: '#FFF',
      minWidth: '150px',
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        '&:last-child': {
          marginTop: theme.spacing(2),
        },
      },
    },
    buttonContainer: {
      display: 'flex',
      width: '100%',
      marginTop: theme.spacing(2),
      flexWrap: 'wrap',
    },
    spacer: {
      flex: 1,
    },
    checkbox: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(3),
      float: 'right',
    },
    recurringMessage: {
      textAlign: 'center',
      marginTop: theme.spacing(2),
      pageBreakBefore: 'auto',
      pageBreakInside: 'avoid',
      pageBreakAfter: 'auto',
    },
    '@media print': {
      buttonContainer: {
        display: 'none',
      },
      checkbox: {
        display: 'none',
      },
      divider: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
      },
      root: {
                marginBottom: theme.spacing(0.5),
            }
    },
  })
);

type LoadingState = { type: 'Loading' };
type ErrorState = { type: 'Error'; pageError: PageError };
type LoadedState = {
  type: 'Loaded';
  invoice: InvoiceDTO;
  userInfo: UserInfo;
  paymentInProgress: boolean;
  showPayButton: boolean;
  isAmountOverCardLimit: boolean;
};

type InvoiceState = LoadingState | ErrorState | LoadedState;

const Invoice: React.FunctionComponent = () => {
  const classes = useStyles();
  const { id, authNumber } = useParams<UriParams>();
  const { publicApiClient } = useContext(ApiContext)!;
  const billingApi = new BillingApi(publicApiClient);
  const { identityConfig, invoicePortalUrl, invoiceApiUrl } = useConfiguration()!;
  const cardPaymentsApi = useMemo(() => new CardPaymentsApi(publicApiClient), [publicApiClient]);
  const [state, setState] = useState<InvoiceState>({ type: 'Loading' });
  const authentication = useAuthentication();
  const { setInvoice } = useInvoice();

  useMount(() => {
    getAppInsights().trackEvent({
      name: 'Viewing Invoice for InvoiceId: ' + id,
      properties: { checkoutId: id },
    });
    (async () => {
      try {
        const invoice = await billingApi.GetInvoice(id, authNumber);
        setInvoice(invoice);
        const { type, outstandingAmount, isPaymentInProgress, isPaymentLinkedToInvoice } = invoice;

        let paymentInProgress = isPaymentInProgress || isPaymentLinkedToInvoice;
        let showPayButton = false;
        let isAmountOverCardLimit = false;

        if (outstandingAmount.amountIncVat > 0) {
          const isCredit = type === InvoiceType.Credit;
          const isCheckoutCompleted = await cardPaymentsApi.IsCompleted(
            id,
            outstandingAmount.amountIncVat
          );

          paymentInProgress =
            isPaymentInProgress || isPaymentLinkedToInvoice || isCheckoutCompleted;

          if (!isCredit && !paymentInProgress) {
            showPayButton = true;
          }

          const cardPaymentLimit = 2500;
          isAmountOverCardLimit =
            outstandingAmount.amountIncVat > cardPaymentLimit && showPayButton;
        }

        setState({
          type: 'Loaded',
          userInfo: await publicApiClient.UserInfo(identityConfig.authority),
          invoice: invoice,
          paymentInProgress,
          showPayButton,
          isAmountOverCardLimit,
        });
      } catch (e) {
        setState({ type: 'Error', pageError: e as PageError });
      }
    })();
  });

  const isStaff = async () => {
    const id = new URLSearchParams(window.location.search).get('id');
    const user = await authentication?.userManager.getUser();
    return id && user?.profile.idp === 'adfs';
  };

  const handleSubmit = async (invoice: InvoiceDTO, userInfo: UserInfo) => {
    setState({ type: 'Loading' });
    try {
      const command = new CheckoutCommand(
        invoice,
        authNumber,
        userInfo.email,
        invoicePortalUrl,
        invoiceApiUrl,
        (await isStaff()) ? 'CardholderNotPresent' : 'CardholderPresent'
      );

      const { url } = await cardPaymentsApi.CreateCheckout(command);
      window.location.assign(url);
    } catch (e) {
      setState({ type: 'Error', pageError: e as PageError });
    }
  };

  switch (state.type) {
    case 'Loading':
      return <LoadingSpinner />;
    case 'Error':
      return <ErrorMessage data={state.pageError} />;
    case 'Loaded':
      const { invoice, userInfo, paymentInProgress, showPayButton, isAmountOverCardLimit } = state;
      const { type, itemsPaidByCard, isGuest, isPaymentLinkedToASuspendedService } = invoice;
      const isProForma = type === InvoiceType.ProForma;
      const isCredit = type === InvoiceType.Credit;
      const hasItemsPaidByCard = itemsPaidByCard > 0;

      return (
        <div className={classes.root}>
          <PaperColumn isCredit={isCredit}>
            <Typography variant="h5" gutterBottom className={classes.header}>
              {paymentInProgress && (
                <Alert severity="success" className={classes.alert}>
                  A payment is currently being processed for this invoice.
                </Alert>
              )}
            </Typography>

            {isPaymentLinkedToASuspendedService && paymentInProgress && (
              <Typography variant="h5" gutterBottom className={classes.header}>
                <Alert severity="info" className={classes.alert}>
                {PaymentMessages.SERVICE_LIFT_MESSAGE}
                  
                </Alert>
              </Typography>
            )}

            {isGuest ? (
              <GuestInvoiceContent invoice={invoice} />
            ) : (
              <InvoiceContent invoice={invoice} />
            )}

            {showPayButton && hasItemsPaidByCard && (
              <Typography variant="subtitle2" className={classes.recurringMessage}>
                There are currently no automated recurring payments set up for the items on this
                invoice. If you would like to set up a Direct Debit or automated card payment please
                visit the Customer Portal or alternatively contact the Billing team on 01706 902001.
              </Typography>
            )}

            {isAmountOverCardLimit && <AmountOverCardLimitWarning />}

            <Box className={classes.buttonContainer}>
              <Button
                size="medium"
                variant="contained"
                color="primary"
                className={classes.button}
                onClick={() => window.print()}
              >
                Print
              </Button>
              <div className={classes.spacer} />
              {showPayButton && (
                <Button
                  name="submitForm"
                  size="medium"
                  variant="contained"
                  color="secondary"
                  type="submit"
                  className={classes.button}
                  onClick={() => handleSubmit(invoice, userInfo)}
                  disabled={isAmountOverCardLimit}
                >
                  Pay Invoice
                </Button>
              )}
            </Box>
            <Divider variant="middle" className={classes.divider} />
            <InvoiceHeader showVatRegNo={!isProForma} />
          </PaperColumn>
        </div>
      );
  }
};

export default withTransaction('Invoice', 'component')(Invoice);
